import React, { useState } from 'react';
import { Alert, Button, Checkbox, Form, Modal, Radio, Select, Space, Switch, Table, Tag, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { EmploymentType, User, UserStatus } from '../../../../types/user.types';
import { RolesEnum } from '../../../../types/route.types';
import { DepartmentDto } from '../../../../types/department.types';
import { Store } from '../../../../types/shop.types';
import { BulkListMode, BulkUpdateResult, BulkUserChanges } from '../../../../types/userBulk.types';
import userService from '../../../../services/user/user.service';
import { handleApiError } from '../../../../utils/errorHandler';

interface EmployeeBulkEditModalProps {
  open: boolean;
  onClose: () => void;
  onDone: (result: BulkUpdateResult) => Promise<void> | void;
  selectedUsers: User[];
  departments: DepartmentDto[];
  shops: Store[];
  currentUser: User;
}

const LIST_MODE_OPTIONS: { value: BulkListMode; label: string }[] = [
  { value: 'replace', label: 'Değiştir' },
  { value: 'add', label: 'Ekle' },
  { value: 'remove', label: 'Çıkar' },
];

const INITIAL_VALUES = {
  departmentMode: 'replace',
  branchMode: 'replace',
  isFixedShift: true,
  employeeType: EmploymentType.OFFICE,
  status: UserStatus.ACTIVE,
  role: RolesEnum.USER,
  ipControl: true,
  isFaceIdAllow: true,
};

const EmployeeBulkEditModal: React.FC<EmployeeBulkEditModalProps> = ({
  open,
  onClose,
  onDone,
  selectedUsers,
  departments,
  shops,
  currentUser,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<BulkUpdateResult | null>(null);
  const watched = Form.useWatch([], form) || {};
  // Rol ve güvenlik ayarları sadece admin tarafından toplu değiştirilebilir (backend de aynı kuralı uygular)
  const isAdmin = currentUser.role === RolesEnum.ADMIN;

  const departmentOptions = departments.map(d => ({ value: d.id, label: d.name }));
  const branchOptions = shops.map(s => ({ value: s.id, label: s.name }));
  const primaryDepartmentOptions = departmentOptions.filter(option =>
    (watched.departmentIds || []).includes(option.value)
  );

  const reset = () => {
    form.resetFields();
    setResult(null);
    setSaving(false);
  };

  const handleSubmit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const changes: BulkUserChanges = {};
    if (values.enableDepartments) {
      changes.departments = { mode: values.departmentMode, ids: values.departmentIds };
      if (values.departmentMode !== 'remove' && values.primaryDepartmentId) {
        changes.primaryDepartmentId = values.primaryDepartmentId;
      }
    }
    if (values.enableBranches) changes.branches = { mode: values.branchMode, ids: values.branchIds };
    if (values.enableShift) changes.isFixedShift = values.isFixedShift;
    if (values.enableEmployeeType) changes.employeeType = values.employeeType;
    if (values.enableStatus) changes.status = values.status;
    if (isAdmin && values.enableRole) changes.role = values.role;
    if (isAdmin && values.enableIpControl) changes.ipControl = !!values.ipControl;
    if (isAdmin && values.enableFaceId) changes.isFaceIdAllow = !!values.isFaceIdAllow;

    if (Object.keys(changes).length === 0) {
      message.warning('En az bir değişiklik seçin.');
      return;
    }

    setSaving(true);
    try {
      const response = await userService.bulkUpdateUsers(selectedUsers.map(u => u.id), changes);
      setResult(response);
      if (response.failed === 0) {
        message.success(`${response.succeeded} çalışan güncellendi.`);
      } else {
        message.warning(`${response.succeeded} çalışan güncellendi, ${response.failed} çalışan güncellenemedi.`);
      }
      await onDone(response);
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (name: string, label: string) => (
    <Form.Item name={name} valuePropName="checked" className="mb-2">
      <Checkbox>
        <strong>{label}</strong>
      </Checkbox>
    </Form.Item>
  );

  const listSection = (
    prefix: 'department' | 'branch',
    options: { value: number; label: string }[],
    placeholder: string
  ) => (
    <div className="ps-4 mb-3">
      <Form.Item name={`${prefix}Mode`} className="mb-2">
        <Radio.Group options={LIST_MODE_OPTIONS} optionType="button" buttonStyle="solid" size="small" />
      </Form.Item>
      <Form.Item
        name={`${prefix}Ids`}
        className="mb-2"
        rules={[{ required: true, message: `${placeholder} seçin` }]}
      >
        <Select
          mode="multiple"
          allowClear
          placeholder={placeholder}
          options={options}
          optionFilterProp="label"
        />
      </Form.Item>
    </div>
  );

  const renderResult = () => {
    if (!result) return null;
    const rows = [...result.results].sort((a, b) => Number(a.success) - Number(b.success));
    return (
      <>
        <Space wrap className="mb-3">
          <Tag>Toplam: {result.total}</Tag>
          <Tag color="green">Güncellendi: {result.succeeded}</Tag>
          {result.failed > 0 && <Tag color="red">Güncellenemedi: {result.failed}</Tag>}
        </Space>
        <Table
          rowKey="id"
          size="small"
          dataSource={rows}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          columns={[
            { title: 'Çalışan', dataIndex: 'name', key: 'name' },
            {
              title: 'Sonuç',
              key: 'status',
              render: (_: unknown, row) =>
                row.success ? (
                  <Tag color="green">Güncellendi</Tag>
                ) : (
                  <>
                    <Tag color="red">Güncellenemedi</Tag>
                    <div className="text-danger small mt-1">{row.message}</div>
                  </>
                ),
            },
          ]}
        />
      </>
    );
  };

  return (
    <Modal
      title={`Toplu Değiştir (${selectedUsers.length} çalışan)`}
      open={open}
      onCancel={() => !saving && onClose()}
      afterClose={reset}
      maskClosable={!saving}
      closable={!saving}
      width={720}
      footer={
        result ? (
          <Button type="primary" onClick={onClose}>
            Kapat
          </Button>
        ) : (
          <Space>
            <Button onClick={onClose} disabled={saving}>
              İptal
            </Button>
            <Button type="primary" onClick={handleSubmit} loading={saving}>
              {selectedUsers.length} Çalışanı Güncelle
            </Button>
          </Space>
        )
      }
    >
      {result ? (
        renderResult()
      ) : (
        <>
          <div className="mb-3">
            {selectedUsers.slice(0, 8).map(u => (
              <Tag key={u.id} className="mb-1">
                {u.firstName} {u.lastName}
              </Tag>
            ))}
            {selectedUsers.length > 8 && <Tag className="mb-1">+{selectedUsers.length - 8} kişi</Tag>}
          </div>
          <Alert
            type="info"
            showIcon
            className="mb-3"
            message="Sadece işaretlediğin alanlar değişir, diğer bilgiler olduğu gibi kalır."
          />

          <Form form={form} layout="vertical" initialValues={INITIAL_VALUES}>
            {toggle('enableDepartments', 'Departman')}
            {watched.enableDepartments && (
              <>
                {listSection('department', departmentOptions, 'Departman')}
                {watched.departmentMode !== 'remove' && (
                  <Form.Item name="primaryDepartmentId" label="Ana departman (opsiyonel)" className="ps-4">
                    <Select allowClear placeholder="Seçilen departmanlardan biri" options={primaryDepartmentOptions} />
                  </Form.Item>
                )}
              </>
            )}

            {toggle('enableBranches', 'Şube')}
            {watched.enableBranches && listSection('branch', branchOptions, 'Şube')}

            {toggle('enableShift', 'Vardiya tipi')}
            {watched.enableShift && (
              <Form.Item name="isFixedShift" className="ps-4">
                <Select
                  options={[
                    { value: true, label: t('employees.modals.fixedShift') },
                    { value: false, label: t('employees.modals.shiftBased') },
                  ]}
                />
              </Form.Item>
            )}

            {toggle('enableEmployeeType', 'Çalışma alanı')}
            {watched.enableEmployeeType && (
              <Form.Item name="employeeType" className="ps-4">
                <Select
                  options={[
                    { value: EmploymentType.OFFICE, label: 'Ofis' },
                    { value: EmploymentType.FIELD, label: 'Saha' },
                    { value: EmploymentType.HYBRID, label: 'Hibrit (Saha + Ofis)' },
                  ]}
                />
              </Form.Item>
            )}

            {toggle('enableStatus', 'Durum')}
            {watched.enableStatus && (
              <>
                <Form.Item name="status" className="ps-4 mb-2">
                  <Select
                    options={[
                      { value: UserStatus.ACTIVE, label: 'Aktif (Çalışıyor)' },
                      { value: UserStatus.RESIGNED, label: 'İşten Ayrıldı' },
                    ]}
                  />
                </Form.Item>
                {watched.status === UserStatus.RESIGNED && (
                  <Alert
                    type="warning"
                    showIcon
                    className="ms-4 mb-3"
                    message="Ayrıldı yapılanların cihaz eşleşmesi kaldırılır. İade edilmemiş zimmeti olanlar güncellenmez ve sonuçta listelenir."
                  />
                )}
              </>
            )}

            {isAdmin && (
              <>
                {toggle('enableRole', 'Rol')}
                {watched.enableRole && (
                  <>
                    <Form.Item name="role" className="ps-4 mb-2">
                      <Select
                        options={[
                          { value: RolesEnum.USER, label: t('employees.modals.employee') },
                          { value: RolesEnum.MANAGER, label: t('employees.modals.manager') },
                          { value: RolesEnum.ADMIN, label: t('employees.modals.admin') },
                        ]}
                      />
                    </Form.Item>
                    <Alert
                      type="warning"
                      showIcon
                      className="ms-4 mb-3"
                      message="Rolü değişen çalışanların yetkileri yeni rolün varsayılanlarına sıfırlanır."
                    />
                  </>
                )}

                {toggle('enableIpControl', 'IP kontrolü')}
                {watched.enableIpControl && (
                  <Form.Item name="ipControl" valuePropName="checked" className="ps-4">
                    <Switch checkedChildren="Açık" unCheckedChildren="Kapalı" />
                  </Form.Item>
                )}

                {toggle('enableFaceId', 'Yüz tanıma')}
                {watched.enableFaceId && (
                  <Form.Item name="isFaceIdAllow" valuePropName="checked" className="ps-4">
                    <Switch checkedChildren="Açık" unCheckedChildren="Kapalı" />
                  </Form.Item>
                )}
              </>
            )}
          </Form>
        </>
      )}
    </Modal>
  );
};

export default EmployeeBulkEditModal;
